/**
 * Sets up the workspace a new user owns.
 *
 * Runs straight after `fetchAccount`, because the name on the workspace comes
 * from whoami rather than from anything the sign-up screens collected.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../types/auth.types";
import type {
  CreateWorkspaceInput,
  CreateWorkspacePayload,
  CreateWorkspaceResponse,
  MembershipResponse,
  WorkspaceMembership,
} from "../types/onboarding.types";

/**
 * The part of the payload no screen collects yet. Fixed here rather than
 * inlined at the call site, so the onboarding form that eventually asks for
 * these has one place to replace.
 */
export const WORKSPACE_DEFAULTS = {
  currency: "INR",
  timezone: "Asia/Kolkata",
  /** April - the Indian financial year. */
  financialYearStartMonth: 4,
} as const;

const toMembership = (response: MembershipResponse): WorkspaceMembership => ({
  role: response.role,
  workspace: {
    publicId: response.workspace.public_id,
    name: response.workspace.name,
    slug: response.workspace.slug,
    timezone: response.workspace.timezone,
    currency: response.workspace.currency,
    financialYearStartMonth: response.workspace.financial_year_start_month,
  },
});

/**
 * Creates the workspace for the signed-in user and returns every membership
 * they hold afterwards - the new one included.
 *
 * Needs an active session, like everything else behind a bearer token.
 */
export async function createWorkspace(
  getToken: GetToken,
  input: CreateWorkspaceInput,
  signal?: AbortSignal
): Promise<WorkspaceMembership[]> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot create a workspace.");
  }

  const payload: CreateWorkspacePayload = {
    practice_name: input.practiceName,
    currency: WORKSPACE_DEFAULTS.currency,
    timezone: WORKSPACE_DEFAULTS.timezone,
    financial_year_start_month: WORKSPACE_DEFAULTS.financialYearStartMonth,
    full_name: input.fullName,
  };

  const response = await api.post<CreateWorkspaceResponse>(
    API_ENDPOINTS.onboarding.workspace,
    payload,
    { token, signal }
  );

  return (response.memberships ?? []).map(toMembership);
}
